import React, { useId, useState } from "react";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import { InputField } from "components/fields";
import ButtonOrderRow from "./ButtonOrderRow";
import ColorField from "./ColorField";
import {
  ACTION_BUTTONS,
  BUTTON_STYLES,
  DEFAULT_BUTTON_SETTINGS,
  findLink,
  getButtonBgColor,
  getButtonRadius,
  getButtonText,
  getContrastText,
  isButtonVisible,
  isLinkButtonId,
} from "./constants";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const ButtonChip = ({ color, radius, children }) => (
  <div
    className="flex h-10 min-w-0 flex-1 items-center justify-center truncate px-3 text-sm font-medium"
    style={{ backgroundColor: color, color: getContrastText(color), borderRadius: radius }}
  >
    {children}
  </div>
);

const ButtonsSection = ({
  buttonOrder,
  buttonSettings,
  buttonStyle,
  buttonColor,
  links,
  onFieldChange,
  onButtonSettingChange,
}) => {
  const baseId = useId();
  const [draggedButton, setDraggedButton] = useState(null);
  const [editingButton, setEditingButton] = useState(null);
  const radius = getButtonRadius(buttonStyle);

  const reorder = (buttonId, targetIndex) => {
    const newOrder = [...buttonOrder];
    newOrder.splice(newOrder.indexOf(buttonId), 1);
    newOrder.splice(targetIndex, 0, buttonId);
    onFieldChange("buttonOrder", newOrder);
  };

  const handleDragStart = (e, buttonId) => {
    setDraggedButton(buttonId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, buttonId) => {
    e.preventDefault();
    if (!draggedButton || draggedButton === buttonId) return;
    reorder(draggedButton, buttonOrder.indexOf(buttonId));
  };

  const moveButton = (buttonId, direction) => {
    const currentIndex = buttonOrder.indexOf(buttonId);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= buttonOrder.length) return;
    reorder(buttonId, newIndex);
  };

  const renderRow = (buttonId, index) => {
    const isLink = isLinkButtonId(buttonId);
    const link = isLink ? findLink(links, buttonId) : null;
    const action = isLink ? null : ACTION_BUTTONS.find((b) => b.id === buttonId);
    if (!link && !action) return null;

    const title = isLink ? link.title : getButtonText(buttonSettings, buttonId);
    const color = getButtonBgColor(buttonSettings, buttonColor, buttonId);
    const visible = isButtonVisible(buttonSettings, buttonId);
    const editing = editingButton === buttonId;
    const colorField = (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <ColorField
          label="Button Color"
          value={color}
          onChange={(value) => onButtonSettingChange(buttonId, { color: value })}
          placeholder="#FFFFFF"
          className="sm:w-56"
        />
        <ButtonChip color={color} radius={radius}>
          {title}
        </ButtonChip>
      </div>
    );

    return (
      <ButtonOrderRow
        key={buttonId}
        title={title}
        subtitle={isLink ? "External link · Manage on the Links page" : action.description}
        swatchColor={color}
        visible={visible}
        dragging={draggedButton === buttonId}
        isFirst={index === 0}
        isLast={index === buttonOrder.length - 1}
        editing={editing}
        editLabel={isLink ? `Edit color for “${title}”` : `Edit “${title}”`}
        editPanelId={`${baseId}-${buttonId}-edit`}
        onDragStart={(e) => handleDragStart(e, buttonId)}
        onDragOver={(e) => handleDragOver(e, buttonId)}
        onDragEnd={() => setDraggedButton(null)}
        onMove={(direction) => moveButton(buttonId, direction)}
        onToggleVisibility={() => onButtonSettingChange(buttonId, { visible: !visible })}
        onToggleEdit={() => setEditingButton(editing ? null : buttonId)}
      >
        {isLink ? (
          <>
            {colorField}
            <p className="text-xs text-fg-muted">
              Edit the link title or URL on the Links page.
            </p>
          </>
        ) : (
          <>
            <InputField
              label="Button Text"
              value={title}
              onChange={(e) => onButtonSettingChange(buttonId, { text: e.target.value })}
              placeholder={DEFAULT_BUTTON_SETTINGS[buttonId]?.text}
            />
            {colorField}
            {buttonId === "giving" && (
              <InputField
                type="url"
                label="Mobile Giving Page URL"
                value={buttonSettings?.giving?.url || ""}
                onChange={(e) => onButtonSettingChange("giving", { url: e.target.value })}
                placeholder="https://pushpay.com/your-church"
                hint="Link to your external giving page (Pushpay, Tithe.ly, etc.). Leave empty to use the built-in donation form."
              />
            )}
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  onButtonSettingChange(buttonId, { text: DEFAULT_BUTTON_SETTINGS[buttonId]?.text })
                }
              >
                Reset to Default
              </Button>
            </div>
          </>
        )}
      </ButtonOrderRow>
    );
  };

  return (
    <>
      <Card padding="md" className="gap-5">
        <CardHeader title="Button Style" />
        <div role="group" aria-labelledby={`${baseId}-shape`}>
          <p id={`${baseId}-shape`} className="mb-1.5 text-sm font-medium text-fg">
            Shape
          </p>
          <div className="grid grid-cols-3 gap-3">
            {BUTTON_STYLES.map((style) => {
              const selected = buttonStyle === style.value;
              return (
                <button
                  key={style.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onFieldChange("buttonStyle", style.value)}
                  className={cx(
                    "focus-ring flex flex-col items-center gap-2 rounded-control border-2 p-4 transition-colors",
                    selected
                      ? "border-primary bg-brand-50 dark:bg-brand-900/40"
                      : "border-line hover:bg-surface-subtle"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="h-10 w-full"
                    style={{ backgroundColor: buttonColor, borderRadius: style.radius }}
                  />
                  <span className="text-sm font-medium text-fg">{style.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <Card padding="md" className="gap-5">
        <CardHeader
          title="Action Buttons"
          description="Drag to reorder all buttons and links. Manage link content on the Links page."
        />
        <ul
          aria-label="Button order"
          className="divide-y divide-line rounded-control border border-line"
        >
          {buttonOrder.map(renderRow)}
        </ul>
      </Card>
    </>
  );
};

export default ButtonsSection;
